// Fatura ekranlarının (liste InvoicesPage + tam-sayfa InvoiceDetailPage) paylaştığı
// saf yardımcılar ve sabitler. Buradaki hiçbir şey React state'ine bağlı değildir;
// hesap mantığı (recalcDetail / getCollectionSummary) tek kaynaktan beslenir.

export const API_BASE = '/api/invoices';

// #252 Genel Parametre anahtarı: sipariş/fatura kalem rehberinde pasif ürünler gösterilsin mi.
export const SETTING_SHOW_PASSIVE_PRODUCTS = 'Product.ShowPassiveInSelection';

// Bir bool Setting'i (Key/Value) okur; satır yoksa/okunamazsa defaultVal döner.
export async function fetchBoolSetting(key, headers, defaultVal = false) {
  try {
    const url = `/api/settings?$filter=${encodeURIComponent(`Key eq '${key}'`)}&$top=1`;
    const res = await fetch(url, { headers });
    if (res.ok) {
      const j = await res.json();
      const rows = j?.value || j?.data || j?.Data || (Array.isArray(j) ? j : []);
      const v = rows[0]?.value ?? rows[0]?.Value;
      if (v != null && v !== '') return String(v).toLowerCase() === 'true' || String(v) === '1';
    }
  } catch { /* sessiz */ }
  return defaultVal;
}

export const EMPTY_FORM = {
  textId: '', printedNo: '', printedType: '', receiptNo: '', erpId: '', specialId: '',
  customerId: '', representativeId: '', vendorId: '', warehouseId: '',
  deliveryAdress: '', description1: '', description2: '',
  documentDate: '', dueDate: '', shipDate: '', dueDay: '', year: '',
  type: 0, paymentType: '', paymentTypeId: '', eArchive: '', status: '',
  grossAmount: '', netAmount: '', discountAmount: '',
  discountPercentage1: '', discountPercentage2: '', discountPercentage3: '',
  vatBase: '', vatAmount: '', specialConsumptionTaxAmount: '',
  orderId: '', dispatchId: '',
  // #257 Durum (yaşam döngüsü) ve Onay Durumu — "Kaydet"/"Taslak Kaydet" ve Onayla düğmeleri belirler.
  lifecycleStatus: '', approvalStatus: 0,
};

export const EMPTY_DETAIL = {
  id: '',
  clientId: '',
  productId: '',
  warehouseId: '',
  quantity: '1',
  unitOrder: '',
  unitPrice: '',
  // #249 Satır birimi (ürünün tanımlı birimlerinden seçilen ad, ör. "AD"/"PK"). Boş = baz birim.
  unit: '',
  unitNetPrice: '',
  unitVatPrice: '',
  grossAmount: '',
  netAmount: '',
  discountAmount: '',
  discountPercentage1: '',
  discountPercentage2: '',
  discountPercentage3: '',
  discountPercentage4: '',
  discountPercentage5: '',
  discountPercentage6: '',
  discountPercentage7: '',
  discountPercentage8: '',
  vatBase: '',
  vatAmount: '',
  // #760 Kural 1: KDV oranı ilk açılışta ve her yeni satırda BOŞ gelir. Eskiden sabit '20' idi ve
  // ürünün fiyat tanımı yoksa o değer olduğu gibi kalıyordu — kullanıcı hiç dokunmadığı hâlde belgeye
  // %20 yazılıyordu (ve %0/%10'luk ürünlerde tutar SESSİZCE yanlış hesaplanıyordu).
  vatPercentage: '',
  // #241 Ürünün min. sipariş adedi (katları kuralı) — satır eklenirken üründen kopyalanır.
  minOrderQuantity: '',
};

/**
 * #760 KALEM KDV ORANININ KAYNAĞI — TEK YERDE (fatura / sipariş / irsaliye aynı kuralı uygular).
 *
 * Melih'in sıralaması:
 *   1) Yeni satır → BOŞ (bkz. EMPTY_DETAIL.vatPercentage).
 *   2) Fiyat tanımı VAR ve KDV oranı tanımlı → fiyat tanımındaki oran.
 *   3) Fiyat tanımı VAR, KDV oranı tanımlı DEĞİL → ÜRÜN KARTINDAKİ oran.
 *   4) Fiyat tanımı YOK → ÜRÜN KARTINDAKİ oran.
 *
 * ⚠ 3. kuralın veri düzeyindeki sınırı: `Price.VatPercentage` non-nullable decimal olduğu için
 * "tanımlanmamış" ile "%0 olarak tanımlanmış" AYNI değere (0) düşer. Kuralın kastı "boş bırakılmış"
 * olduğundan 0, tanımsız sayılır ve ürün kartına düşülür. Pratikte sonuç aynıdır (ürün kartı da 0'sa
 * 0 yazılır); tek istisna, kartı %20 olan bir ürüne BİLEREK %0 fiyat tanımlanmış olmasıdır — o senaryo
 * ancak Price.VatPercentage nullable yapılırsa ayrıştırılabilir (şema kararı).
 *
 * @returns {string|null} yazılacak KDV oranı (string) ya da null → alan BOŞ bırakılır.
 */
export function resolveLineVat(price, productVat) {
  const priceVat = price == null ? null : Number(price.vatPercentage ?? price.VatPercentage);
  if (priceVat != null && Number.isFinite(priceVat) && priceVat > 0) return String(priceVat);

  const prodVat = productVat == null || productVat === '' ? null : Number(productVat);
  if (prodVat != null && Number.isFinite(prodVat)) return String(prodVat);

  return null;
}

/*
 * #797/#799/#805 `invoiceStockSign` / `waybillStockSign` KALDIRILDI.
 *
 * Bu iki tablo, sunucudaki kanonik yön matrisinin (DataScope.Entities/Enums/DocumentEffect.cs →
 * `DocumentEffects`) web'e kopyasıydı ve yalnız "kaydetmeden önce stoğu düşmüş göster" tahmini için
 * vardı. Tahmin kaldırıldı (bkz. stockBalance.js): stok hücresi artık yalnız defterdeki kayıtlı
 * bakiyeyi gösterir, belge kaydedilince yeniden okunur. Yönü web'de bilmeye gerek kalmadı — ve
 * kopyanın sunucudaki matrisle ayrışma riski de ortadan kalktı.
 */

/**
 * #760 (2. tur) FİYAT ÇÖZÜLEMEDİĞİNDE kalemin KDV'sini ürün kartındaki orandan yazar.
 *
 * İlk turda yalnız "fiyat tanımı yok" (200 + boş gövde) dalı bu fallback'i uyguluyordu; fiyat ucunun
 * HATA dönmesi (403 kapsam dışı müşteri, 5xx) ve ağ hatası dallarında sessizce dönülüyor ve kalem
 * KDV'siz kalıyordu. Kullanıcı açısından sonuç "KDV hiçbir koşulda gelmiyor"du. Üç dal da KDV
 * bakımından aynıdır: oran ürün kartından gelir. Fiyat alanına DOKUNULMAZ — o elle girilecektir.
 */
export function applyVatFallback(applyFn, productVat) {
  const vat = resolveLineVat(null, productVat);
  if (vat != null) applyFn({ vatPercentage: vat });
}

/**
 * #760 Ürün kartındaki KDV oranı. ALIŞ belgesinde alış oranı, diğer her belgede satış oranı geçerlidir —
 * ürün kartı ikisini AYRI tutar ve karıştırmak alış faturasında yanlış matrah demektir.
 * Lookup öğesi camelCase ya da PascalCase gelebilir (farklı uçlar); ikisi de okunur.
 */
export function productVatOf(item, isPurchase = false) {
  if (!item) return null;
  const v = isPurchase
    ? (item.purchaseVatPercentage ?? item.PurchaseVatPercentage)
    : (item.salesVatPercentage ?? item.SalesVatPercentage);
  return v ?? null;
}

export const STATUS_OPTIONS = [
  { value: 0, label: 'Taslak' },
  { value: 1, label: 'Onaylandı' },
  { value: 2, label: 'İptal' },
];

// #257 Belge yaşam döngüsü (Durum kolonu). Eski `Status` int'inden ayrı LifecycleStatus kolonu.
export const LIFECYCLE_STATUS_OPTIONS = [
  { value: 0, label: 'Taslak' },
  { value: 1, label: 'Aktif' },
  { value: 2, label: 'Pasif' },
  { value: 3, label: 'Silindi' },
];

// #257 Onay Durumu kolonu (ApprovalStatus). Faturalandır/Onayla → Onaylı.
export const APPROVAL_STATUS_OPTIONS = [
  { value: 0, label: 'Beklemede' },
  { value: 1, label: 'Onaylı' },
  { value: 2, label: 'Reddedildi' },
];

/**
 * İade alt-türleri (Type 2/3/4) — sipariş, irsaliye ve faturada AYNI enum.
 *
 * Talep #833-835: bu ayrım artık FORMDA SEÇİLMEZ, MENÜDEN gelir. Her alt-türün kendi listesi ve
 * kendi rota öneki vardır; "Yeni" o türle açılır ve belge o türe sabitlenir. Liste burada yalnız
 * etiket kaynağı olarak kalır (detay ekranı türü salt-okunur gösterir, kolonlar okur).
 */
export const RETURN_SUBTYPES = [
  { value: 2, label: 'Müşteriden Sağlam İade' },
  { value: 3, label: 'Satıcıya İade' },
  { value: 4, label: 'Müşteriden Bozuk İade' },
];

export const returnSubtypeLabel = (type) =>
  (RETURN_SUBTYPES.find((o) => o.value === Number(type)) || {}).label || '-';

// Aynı Fatura ekranı, menü konumuna göre Tür'e (Invoice.Type) göre filtreli açılır (Satış/Alış/İade).
// Tür ekranda gösterilmez; her varyant kendi Type değerini otomatik set eder/filtreler.
export const INVOICE_VARIANTS = {
  sales: {
    baseFilter: 'Type eq 0', types: [0],
    title: 'Faturalar', subtitle: 'Satış faturalarını filtreleyin, sıralayın ve yönetin.', newLabel: '+ Yeni Fatura',
    kicker: 'Satış Faturası',
  },
  purchase: {
    baseFilter: 'Type eq 1', types: [1],
    title: 'Alış Faturaları', subtitle: 'Alış faturalarını filtreleyin, sıralayın ve yönetin.', newLabel: '+ Yeni Alış Faturası',
    kicker: 'Alış Faturası',
  },
  // #835 — üç iade türü artık AYRI menü/ekrandır. `return` varyantı yalnız eski bağlantılar
  // (bookmark, e-posta) kırılmasın diye korunur; menüden erişilmez.
  return: {
    baseFilter: 'Type eq 2 or Type eq 3 or Type eq 4', types: [2, 3, 4],
    title: 'İade Faturaları', subtitle: 'İade faturalarını filtreleyin, sıralayın ve yönetin.', newLabel: '+ Yeni İade Faturası',
    kicker: 'İade Faturası',
  },
  returnGood: {
    baseFilter: 'Type eq 2', types: [2],
    title: 'Müşteriden Sağlam İade', subtitle: 'Müşteriden sağlam iade faturalarını filtreleyin, sıralayın ve yönetin.',
    newLabel: '+ Yeni Sağlam İade Faturası', kicker: 'Müşteriden Sağlam İade Faturası',
  },
  returnDamaged: {
    baseFilter: 'Type eq 4', types: [4],
    title: 'Müşteriden Bozuk İade', subtitle: 'Müşteriden bozuk iade faturalarını filtreleyin, sıralayın ve yönetin.',
    newLabel: '+ Yeni Bozuk İade Faturası', kicker: 'Müşteriden Bozuk İade Faturası',
  },
  returnVendor: {
    baseFilter: 'Type eq 3', types: [3],
    title: 'Satıcıya İade', subtitle: 'Satıcıya iade faturalarını filtreleyin, sıralayın ve yönetin.',
    newLabel: '+ Yeni Satıcıya İade Faturası', kicker: 'Satıcıya İade Faturası',
  },
  serviceGiven: {
    baseFilter: 'Type eq 5', types: [5],
    title: 'Verilen Hizmet', subtitle: 'Verilen hizmet faturalarını filtreleyin, sıralayın ve yönetin.', newLabel: '+ Yeni Hizmet Faturası',
    kicker: 'Verilen Hizmet Faturası',
  },
  serviceReceived: {
    baseFilter: 'Type eq 6', types: [6],
    title: 'Alınan Hizmet', subtitle: 'Alınan hizmet faturalarını filtreleyin, sıralayın ve yönetin.', newLabel: '+ Yeni Hizmet Faturası',
    kicker: 'Alınan Hizmet Faturası',
  },
};

// Varyantın liste + yeni/detay route öneki. Menü vurgusu (NavLink) URL'e göre çalıştığı için
// her varyantın kendi öneki olmalı; aksi halde tüm faturalar /invoices altında olup Satış
// "Faturalar" menüsü aktif kalır (#Alış'ta yeni açınca yanlış menü vurgusu bug'ı).
export function invoiceBasePath(variant) {
  return {
    sales: '/invoices',
    purchase: '/purchase-invoices',
    return: '/return-invoices',
    // #835 — iade türü başına ayrı önek. Düz (nested değil) tutulur: '/return-invoices/good'
    // olsaydı '/return-invoices/:id' detay rotasıyla çakışırdı.
    returnGood: '/return-invoices-good',
    returnDamaged: '/return-invoices-damaged',
    returnVendor: '/return-invoices-vendor',
    serviceGiven: '/service-invoices-given',
    serviceReceived: '/service-invoices-received',
  }[variant] || '/invoices';
}

// İrsaliye varyant route öneki (aynı menü-vurgusu gerekçesi).
export function waybillBasePath(variant) {
  return {
    sales: '/waybills',
    purchase: '/purchase-waybills',
    return: '/return-waybills',
    // #834
    returnGood: '/return-waybills-good',
    returnDamaged: '/return-waybills-damaged',
    returnVendor: '/return-waybills-vendor',
    consignment: '/consignment-waybills',
    // #830 — Konsinye Alış İrsaliyesi (Type 8).
    consignmentPurchase: '/consignment-purchase-waybills',
  }[variant] || '/waybills';
}

// variant <-> Type eşlemesi (route'tan/satırdan geri çözümleme için).
// #835: iade türleri artık kendi varyantına düşer — bir satıra tıklandığında o türün ekranı açılır.
export function variantForType(type) {
  const t = Number(type);
  if (t === 1) return 'purchase';
  if (t === 2) return 'returnGood';
  if (t === 3) return 'returnVendor';
  if (t === 4) return 'returnDamaged';
  if (t === 5) return 'serviceGiven';
  if (t === 6) return 'serviceReceived';
  return 'sales';
}

export const COLUMN_FILTER_META = {
  textId: { type: 'text', odataField: 'TextId' },
  printedNo: { type: 'text', odataField: 'PrintedNo' },
  documentDate: { type: 'text', odataField: 'DocumentDate' },
  netAmount: { type: 'number', odataField: 'NetAmount' },
  // #257 "Durum" artık yaşam döngüsü (LifecycleStatus); Onay Durumu ayrı kolon.
  lifecycleStatus: { type: 'select', odataField: 'LifecycleStatus', options: LIFECYCLE_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })) },
  approvalStatus: { type: 'select', odataField: 'ApprovalStatus', options: APPROVAL_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })) },
};

// Serbest metin araması yalnız string OData alanları üzerinde (int/guid'e contains yapılmaz).
export const SEARCH_FIELDS = ['TextId', 'PrintedNo', 'ReceiptNo', 'ErpId', 'Description1', 'Description2'];

export const statusLabel = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const found = STATUS_OPTIONS.find((o) => o.value === Number(value));
  return found ? found.label : String(value);
};

export const statusTone = (value) => {
  const v = Number(value);
  if (v === 1) return 'ok';
  if (v === 2) return 'bad';
  return 'neutral';
};

// #257 Durum (yaşam döngüsü) rozeti: Aktif=yeşil, Pasif/Silindi=kırmızı, Taslak=nötr.
export const lifecycleLabel = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const found = LIFECYCLE_STATUS_OPTIONS.find((o) => o.value === Number(value));
  return found ? found.label : String(value);
};

export const lifecycleTone = (value) => {
  const v = Number(value);
  if (v === 1) return 'ok';
  if (v === 2 || v === 3) return 'bad';
  return 'neutral';
};

// #257 Onay Durumu rozeti: Onaylı=yeşil, Reddedildi=kırmızı, Beklemede=nötr.
export const approvalLabel = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const found = APPROVAL_STATUS_OPTIONS.find((o) => o.value === Number(value));
  return found ? found.label : String(value);
};

export const approvalTone = (value) => {
  const v = Number(value);
  if (v === 1) return 'ok';
  if (v === 2) return 'bad';
  return 'neutral';
};

export function toInt(value) {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export function toNullableInt(value) {
  if (value === '' || value == null) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

export function toDecimal(value) {
  const parsed = Number.parseFloat(String(value || '').replace(',', '.'));
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * #250 Ondalık alan girişini temizler: yalnız rakam + tek ondalık ayraç (. veya ,) bırakır,
 * harf/sembol yazımını engeller. Boş string'e izin verir (silme). Fatura/Sipariş kalemlerindeki
 * Miktar, Birim Fiyat, İskonto%, KDV% gibi TÜM decimal alanlarda kullanılır.
 */
export function sanitizeDecimal(value) {
  let s = String(value ?? '').replace(/[^\d.,]/g, '');
  const firstSep = s.search(/[.,]/);
  if (firstSep !== -1) {
    // İlk ayracı koru, sonraki tüm ayraçları at (birden fazla ondalık nokta olamaz).
    s = s.slice(0, firstSep + 1) + s.slice(firstSep + 1).replace(/[.,]/g, '');
  }
  return s;
}

/**
 * #249 Ürünün SATIŞ BİRİMLERİNİ ürün tanımından (ProductUnit tablosu — Ürün Kartı
 * "Birimler & Barkod" bölümü) çeker; sipariş/fatura Birim dropdown'ı bu KANONİK kaynağı
 * kullanır (eski denormalize Product.Unit1..Unit5 skalarları DEĞİL — onlar import'tan
 * kalma bayat olabilir; ürün ekranı da bu endpoint'i kullanır, ikisi tutarlı olsun).
 * Ana birim (IsBase) başa, sonra SortOrder'a göre sıralanır. Tanımlı birim yoksa
 * fallbackBase (genelde ürünün baz birimi) tek elemanlı döner; o da yoksa boş.
 */
export async function fetchProductUnits(productId, headers, fallbackBase) {
  try {
    const res = await fetch(`/api/productunits/ForProduct?productId=${encodeURIComponent(productId)}`, { headers });
    if (res.ok) {
      const j = await res.json();
      const rows = Array.isArray(j) ? j : (j?.data || j?.Data || j?.value || []);
      const parsed = rows
        .map((u) => ({
          name: String(u.unitName ?? u.UnitName ?? '').trim(),
          // #253 Ana birime göre çevrim çarpanı (null/0 → 1). Birim değişince fiyat bununla ölçeklenir.
          multiplier: (() => { const m = Number(u.multiplier ?? u.Multiplier); return Number.isFinite(m) && m > 0 ? m : 1; })(),
          isBase: !!(u.isBase ?? u.IsBase),
          sortOrder: Number(u.sortOrder ?? u.SortOrder ?? 0),
        }))
        .filter((u) => u.name);
      parsed.sort((a, b) => (b.isBase ? 1 : 0) - (a.isBase ? 1 : 0) || a.sortOrder - b.sortOrder);
      // Aynı ada göre tekilleştir (ilk/base kazanır).
      const seen = new Set();
      const out = parsed.filter((u) => (seen.has(u.name) ? false : (seen.add(u.name), true)))
        .map((u) => ({ name: u.name, multiplier: u.multiplier }));
      if (out.length) return out;
    }
  } catch { /* sessiz */ }
  const fb = fallbackBase == null ? '' : String(fallbackBase).trim();
  return fb ? [{ name: fb, multiplier: 1 }] : [];
}

// Birim değişince yeni birim fiyatını hesaplar: taban fiyat = mevcut fiyat / mevcut çarpan;
// yeni fiyat = taban fiyat × yeni çarpan. Çarpan bulunamazsa 1 kabul edilir (#253).
export function convertUnitPrice(currentPrice, units, fromUnit, toUnit) {
  const list = Array.isArray(units) ? units : [];
  const mulOf = (name) => (list.find((u) => u.name === name)?.multiplier) || (list[0]?.multiplier) || 1;
  const fromMul = mulOf(fromUnit || list[0]?.name);
  const toMul = mulOf(toUnit);
  const base = toDecimal(currentPrice) / (fromMul || 1);
  return decimalString(base * toMul);
}

/**
 * #241 Min. Sipariş Adedi (katları) kuralı. Satır miktarı, ürünün minOrderQuantity
 * değerinin pozitif tam katı olmalıdır (örn. 6 → 6, 12, 18; 3 veya 9 olmaz).
 * minQty yoksa/0 ise kısıt yoktur. İhlal varsa taban değeri (number) döner; yoksa null.
 * (Çağıran: dönen değer truthy ise engelle + kullanıcıya izin verilen değerleri göster.)
 */
export function orderMultipleViolation(quantity, minQty) {
  const m = toDecimal(minQty);
  if (!m || m <= 0) return null; // kısıt yok
  const q = toDecimal(quantity);
  if (q <= 0) return null; // miktar>0 kontrolü ayrı yapılır
  const ratio = q / m;
  const rounded = Math.round(ratio);
  // Ondalık kayan-nokta toleransı; ratio tam sayı değilse veya < 1 ise ihlal.
  if (rounded < 1 || Math.abs(ratio - rounded) > 1e-6) return m;
  return null;
}

export function toNullableGuid(value) {
  return value || null;
}

export function toNullableDate(value) {
  return value ? new Date(value).toISOString() : null;
}

export function dateInputValue(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

const moneyFormatter = new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatMoney(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? moneyFormatter.format(parsed) : '-';
}

export function createClientId() {
  return window.crypto?.randomUUID?.() || `detail-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function decimalString(value) {
  const parsed = toDecimal(value);
  return parsed ? String(Number(parsed.toFixed(2))) : '';
}

// Satır hesabı: brüt = miktar*birimfiyat, matrah = brüt - iskonto, kdv = matrah*kdv%, net = matrah + kdv.
export function recalcDetail(row) {
  const quantity = toDecimal(row.quantity);
  const unitPrice = toDecimal(row.unitPrice);
  const discountAmount = toDecimal(row.discountAmount);
  const vatPercentage = toDecimal(row.vatPercentage);
  const grossAmount = quantity * unitPrice;
  const vatBase = Math.max(0, grossAmount - discountAmount);
  const vatAmount = vatBase * vatPercentage / 100;
  const netAmount = vatBase + vatAmount;
  return {
    ...row,
    grossAmount: decimalString(grossAmount),
    vatBase: decimalString(vatBase),
    vatAmount: decimalString(vatAmount),
    netAmount: decimalString(netAmount),
    unitVatPrice: quantity ? decimalString(vatAmount / quantity) : '',
    unitNetPrice: quantity ? decimalString(netAmount / quantity) : '',
  };
}

// İsk% kolonundan iskonto₺'yi türetir (mockup %-öncelikli giriş ↔ mevcut TL-öncelikli model köprüsü).
// discountPercentage1 girilince discountAmount = brüt*%1 hesaplanır, sonra satır yeniden hesaplanır.
export function applyDiscountPercent(row) {
  const quantity = toDecimal(row.quantity);
  const unitPrice = toDecimal(row.unitPrice);
  const pct = toDecimal(row.discountPercentage1);
  const grossAmount = quantity * unitPrice;
  const discountAmount = grossAmount * pct / 100;
  return recalcDetail({ ...row, discountAmount: decimalString(discountAmount) });
}

// v3 kademeli (bileşik) iskonto: brüt'e İsk-1..4% sırayla uygulanır
// (her kademe kalan tutar üzerinden). matrah = Net (KDV Hariç); net = matrah + KDV.
// İskonto₺ = brüt - matrah. Backend'e discountPercentage1-4 + discountAmount gider.
export function recalcLine(row) {
  const quantity = toDecimal(row.quantity);
  const unitPrice = toDecimal(row.unitPrice);
  const vatPercentage = toDecimal(row.vatPercentage);
  const gross = quantity * unitPrice;
  let rem = gross;
  [row.discountPercentage1, row.discountPercentage2, row.discountPercentage3, row.discountPercentage4]
    .forEach((p) => { rem -= rem * ((toDecimal(p)) / 100); });
  const matrah = Math.max(0, rem);
  const discountAmount = gross - matrah;
  const vatAmount = matrah * vatPercentage / 100;
  const netAmount = matrah + vatAmount;
  return {
    ...row,
    grossAmount: decimalString(gross),
    discountAmount: decimalString(discountAmount),
    vatBase: decimalString(matrah),
    vatAmount: decimalString(vatAmount),
    netAmount: decimalString(netAmount),
    unitVatPrice: quantity ? decimalString(vatAmount / quantity) : '',
    unitNetPrice: quantity ? decimalString(netAmount / quantity) : '',
  };
}

export function displayDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('tr-TR');
}

export function formatPercent(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? `%${parsed.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
    : '-';
}

export function normalizeGuid(value) {
  return String(value || '').trim().toLowerCase();
}

export const PAYMENT_TYPE_CODE_BY_TEXT_ID = {
  NAKIT: 1,
  VPOS: 2,
  HAVALE: 3,
  EFT: 3,
  CEK: 4,
  CHECK: 4,
  DBS: 5,
  // #807 Vadeli (açık hesap). Backend InvoicePaymentTypeResolver ile birebir aynı kod.
  VADELI: 6,
  VADE: 6,
};

export function normalizePaymentTypeTextId(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replaceAll('İ', 'I')
    .replaceAll('Ş', 'S')
    .replaceAll('Ğ', 'G')
    .replaceAll('Ü', 'U')
    .replaceAll('Ö', 'O')
    .replaceAll('Ç', 'C')
    .replaceAll('/', '')
    .replaceAll('-', '')
    .replaceAll('_', '')
    .replaceAll(' ', '');
}

export function resolvePaymentTypeCode(item) {
  const normalizedTextId = normalizePaymentTypeTextId(item?.textId);
  if (Object.prototype.hasOwnProperty.call(PAYMENT_TYPE_CODE_BY_TEXT_ID, normalizedTextId)) {
    return PAYMENT_TYPE_CODE_BY_TEXT_ID[normalizedTextId];
  }
  const parsed = Number.parseInt(item?.textId, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

// ─── Talep 833 — Belge ekranı varsayılanları (Fatura / Sipariş / İrsaliye ortak) ──────────────

/**
 * Talep 833 kural 5 — Ödeme tipi lookup'ından NAKİT kaydını bulur.
 *
 * Nakit HARDCODE EDİLMEZ: PaymentType lookup'ı tenant'a göre seed edilir ve hiç seed
 * edilmemiş olabilir (bkz. "Ödeme Tipi lookup seed"). Bulunamazsa null döner ve çağıran
 * alanı BOŞ bırakır — uydurma bir id yazmak, kaydedilemeyen belge üretirdi.
 *
 * Kod 1 = Nakit/Peşin (resolvePaymentTypeCode; backend InvoicePaymentTypeResolver ile aynı).
 * @returns {object|null} lookup item'ı ya da null.
 */
export function findCashPaymentType(paymentTypes) {
  if (!Array.isArray(paymentTypes)) return null;
  return paymentTypes
    .filter((p) => (p.isActive ?? p.IsActive) !== false)
    .find((p) => resolvePaymentTypeCode(p) === 1) || null;
}

/**
 * Talep 833 kural 3 + 4 — Müşteri seçilince belge başlığına uygulanacak varsayılanlar.
 *
 * Temsilci YALNIZ rut ilişkisinden (RoutePlan) çözülür; rut eşleşmesi yoksa representativeId
 * null döner (müşteri kartındaki SalesRepresentativeId'ye düşmez — bkz. DocumentDefaultsController).
 * Depo, çözülen temsilcinin Satış/Sipariş depo parametrelerinden gelir.
 *
 * Uç hata verirse null döner: varsayılan bir KOLAYLIK'tır, alanlar elle doldurulabilir olmalı.
 * @returns {Promise<{representativeId,representativeName,representativeTextId,salesWarehouseId,orderWarehouseId,vendorId}|null>}
 */
export async function fetchDocumentDefaults(customerId, headers) {
  if (!customerId) return null;
  try {
    const res = await fetch(`/api/document-defaults/for-customer/${customerId}`, { headers });
    if (!res.ok) return null;
    const body = await res.json();
    const d = body?.data ?? body?.Data ?? body;
    if (!d) return null;
    return {
      representativeId: d.representativeId ?? d.RepresentativeId ?? '',
      representativeName: d.representativeName ?? d.RepresentativeName ?? '',
      representativeTextId: d.representativeTextId ?? d.RepresentativeTextId ?? '',
      salesWarehouseId: d.salesWarehouseId ?? d.SalesWarehouseId ?? '',
      orderWarehouseId: d.orderWarehouseId ?? d.OrderWarehouseId ?? '',
      vendorId: d.vendorId ?? d.VendorId ?? '',
    };
  } catch {
    return null;
  }
}

export function pickValue(source, ...keys) {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null) return value;
  }
  return null;
}

export function getCollectionSummary(invoiceTotal, paidTotal, payments, dueDate) {
  const safeInvoiceTotal = Math.max(0, toDecimal(invoiceTotal));
  const safePaidTotal = Math.max(0, toDecimal(paidTotal));
  const due = dueDate ? new Date(dueDate) : null;
  const isPastDue = due && !Number.isNaN(due.getTime()) && due < new Date();
  const percent = safeInvoiceTotal > 0
    ? Math.max(0, Math.min(100, (safePaidTotal / safeInvoiceTotal) * 100))
    : 0;
  const lastPaymentDate = (payments || []).reduce((latest, payment) => {
    const value = pickValue(payment, 'paymentDate', 'PaymentDate');
    if (!value) return latest;
    const current = new Date(value);
    if (Number.isNaN(current.getTime())) return latest;
    if (!latest) return current;
    return current > latest ? current : latest;
  }, null);

  if (safeInvoiceTotal > 0 && safePaidTotal >= safeInvoiceTotal) {
    return { label: 'Tahsil Edildi', percent, lastPaymentDate, tone: 'ok' };
  }
  if (safePaidTotal > 0) {
    return { label: 'Kısmi Tahsilat', percent, lastPaymentDate, tone: 'wait' };
  }
  if (isPastDue) {
    return { label: 'Vadesi Geçmiş', percent: 0, lastPaymentDate: null, tone: 'bad' };
  }
  return { label: 'Beklemede', percent: 0, lastPaymentDate: null, tone: 'neutral' };
}

export function lookupLabel(item) {
  if (!item) return '';
  const code = item.textId || item.TextId || item.erpId || item.ErpId || '';
  const desc = item.title || item.Title || item.name || item.Name || item.shortName || item.ShortName
    || item.description || item.Description || item.person || item.Person || '';
  if (desc) return `${code} - ${desc}`.trim().replace(/^- /, '');
  return code || '-';
}
